import { Carousel, CarouselContent, CarouselItem } from "@shopflo/ui/carousel";
import type { CarouselApi } from "@shopflo/ui/carousel";
import { CaretLeft, CaretRight } from "@phosphor-icons/react";
import { useEffect, useState, ReactNode } from "react";
import { IconButton } from "@shopflo/ui/icon-button";
import type { IconButtonProps } from "@shopflo/ui/icon-button";

export interface FullCardCarouselProps {
  images?: string[];
  slides?: ReactNode[];
  onIndexChange?: (index: number) => void;
  activeSlide?: number;
  showDots?: boolean;
  navigationButton?: ReactNode;
  /** Flush to a bottom-sheet shell — no own radius/blur (parent clips the corners). */
  edgeToEdge?: boolean;
}

export const FullCardCarousel = (props: FullCardCarouselProps) => {
  const {
    images,
    slides,
    onIndexChange,
    activeSlide,
    showDots = true,
    navigationButton,
    edgeToEdge = false,
  } = props;

  // Use slides if provided, otherwise fall back to images
  const items = slides || images || [];

  // Default navigation button
  const defaultButton = (
    <CarouselControl
      IconComponent={CaretLeft}
      iconProps={{ weight: "bold", color: "white" }}
    />
  );

  const navButton = navigationButton || defaultButton;

  const [api, setApi] = useState<CarouselApi | null>(null);
  const [selectedIndex, setSelectedIndex] = useState<number>(0);

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      const newIndex = api.selectedScrollSnap();
      setSelectedIndex(newIndex);
      onIndexChange?.(newIndex);
    };
    onSelect();
    api.on("select", onSelect);
    api.on("reInit", onSelect);
    return () => {
      api.off("select", onSelect);
      api.off("reInit", onSelect);
    };
  }, [api, onIndexChange]);

  // Handle programmatic slide changes
  useEffect(() => {
    if (api && activeSlide !== undefined && activeSlide >= 0) {
      api.scrollTo(activeSlide);
      setSelectedIndex(activeSlide);
    }
  }, [activeSlide, api]);

  if (!items.length) return null;

  // Render image-based carousel
  if (images && !slides) {
    return (
      <div className="h-[230px] w-full relative">
        <Carousel
          opts={{
            watchDrag: images.length > 1,
          }}
          className="w-full h-full"
          setApi={setApi}
        >
          <CarouselContent>
            {images.map((image, index) => (
              <CarouselItem key={index}>
                <div
                  className={`h-[230px] w-full bg-slate-50 relative overflow-hidden cursor-pointer ${
                    edgeToEdge ? "" : "rounded-t-lg"
                  }`}
                  style={{
                    backgroundImage: `url(${image})`,
                    backgroundSize: "cover",
                    backgroundPosition: "right",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <div
                    className={`h-full w-full flex items-center justify-center py-3 shadow-[0_0_0_1000px_rgba(0,0,0,0.3)_inset] ${
                      edgeToEdge ? "bg-black/20" : "backdrop-blur-[15px] rounded-lg"
                    }`}>
                    <img
                      src={image}
                      alt={`Product image ${index + 1}`}
                      className="object-contain rounded-lg max-h-[200px] max-w-[280px]"
                    />
                  </div>
                </div>
              </CarouselItem>
            ))}
          </CarouselContent>
        </Carousel>

        {images.length > 1 && (
          <>
            <div
              className="absolute left-2 top-1/2 -translate-y-1/2 z-20"
              onClick={(e) => {
                e?.stopPropagation();
                api?.scrollPrev();
              }}
              role="button"
              aria-label="Previous slide"
            >
              {navButton}
            </div>
            <div
              className="absolute right-2 top-1/2 -translate-y-1/2 z-20 rotate-180"
              onClick={(e) => {
                e?.stopPropagation();
                api?.scrollNext();
              }}
              role="button"
              aria-label="Next slide"
            >
              {navButton}
            </div>

            {showDots && (
              <div className="absolute bottom-1.5 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
                {Array.from({ length: images.length }).map((_, i) => (
                  <button
                    key={i}
                    aria-label={`Go to slide ${i + 1}`}
                    onClick={() => api?.scrollTo?.(i)}
                    aria-current={selectedIndex === i}
                    className={`h-1 rounded-full transition-all duration-300 ease-out ${
                      selectedIndex === i
                        ? "w-[10px] bg-white"
                        : "w-1 bg-white/50"
                    }`}
                  />
                ))}
              </div>
            )}
          </>
        )}
      </div>
    );
  }

  // Render component-based carousel
  return (
    <div className="w-full relative">
      <Carousel
        opts={{
          watchDrag: items.length > 1,
          loop: true,
        }}
        className="w-full"
        setApi={setApi}
      >
        <CarouselContent>
          {items.map((item, index) => (
            <CarouselItem key={index}>
              {typeof item === "string" ? (
                <div
                  className="h-[230px] w-full bg-slate-50 relative rounded-t-lg overflow-hidden cursor-pointer"
                  style={{
                    backgroundImage: `url(${item})`,
                    backgroundSize: "cover",
                    backgroundPosition: "right",
                    backgroundRepeat: "no-repeat",
                  }}
                >
                  <div className="backdrop-blur-[15px] h-full w-full flex items-center justify-center py-3 rounded-lg shadow-[0_0_0_1000px_rgba(0,0,0,0.3)_inset]">
                    <img
                      src={item}
                      alt={`Product image ${index + 1}`}
                      className="object-contain rounded-lg max-h-[200px] max-w-[280px]"
                    />
                  </div>
                </div>
              ) : (
                item
              )}
            </CarouselItem>
          ))}
        </CarouselContent>
      </Carousel>

      {items.length > 1 && (
        <>
          <div
            className="absolute left-[-15px] top-1/2 -translate-y-1/2 z-20"
            onClick={(e) => {
              e?.stopPropagation();
              api?.scrollPrev();
            }}
            role="button"
            aria-label="Previous slide"
          >
            {navButton}
          </div>
          <div
            className="absolute right-[-15px] top-1/2 -translate-y-1/2 z-20 rotate-180"
            onClick={(e) => {
              e?.stopPropagation();
              api?.scrollNext();
            }}
            role="button"
            aria-label="Next slide"
          >
            {navButton}
          </div>

          {showDots && (
            <div className="absolute bottom-2 left-1/2 -translate-x-1/2 z-20 flex items-center gap-1.5">
              {Array.from({ length: items.length }).map((_, i) => (
                <button
                  key={i}
                  aria-label={`Go to slide ${i + 1}`}
                  onClick={() => api?.scrollTo?.(i)}
                  aria-current={selectedIndex === i}
                  className={`h-1 rounded-full transition-all duration-300 ease-out ${
                    selectedIndex === i
                      ? "w-[10px] bg-gray-700"
                      : "w-1 bg-gray-400"
                  }`}
                />
              ))}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export interface CarouselControlProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  IconComponent: IconButtonProps["IconComponent"];
  iconProps?: IconButtonProps["iconProps"];
}

export const CarouselControl: React.FC<CarouselControlProps> = ({
  IconComponent,
  iconProps,
  className,
  ...rest
}) => {
  return (
    <IconButton
      size="sm"
      variant="ghost"
      className={`h-9 w-9 rounded-full ${className ?? ""}`}
      IconComponent={IconComponent}
      iconProps={iconProps}
      {...rest}
    />
  );
};
